import React, { useState, useRef, useEffect } from 'react';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import PropTypes from 'prop-types';

import ControlLabelTooltip from '@/components/common/ControlLabelTooltip';
import {
    twitterShareUrl,
    facebookShareUrl,
    linkedInShareUrl,
    buildShareText,
} from '@/shared/utils/shareUrls';

const PostActions = ({
    likes = 0,
    dislikes = 0,
    comments = 0,
    shares = 0,
    reaction = null,
    isBookmarked = false,
    showingReplies = false,
    user = null,
    shareUrl,
    shareTitle,
    shareCommunityName,
    onLike,
    onDislike,
    onComment,
    onBookmark,
}) => {
    const [copyStatus, setCopyStatus] = useState(null);
    // Track the copy-confirmation reset timer so it can be cleared on unmount
    // (and before scheduling a new one), preventing a setState-after-unmount.
    const copyResetTimer = useRef(null);

    useEffect(() => {
        return () => {
            if (copyResetTimer.current) {
                clearTimeout(copyResetTimer.current);
            }
        };
    }, []);

    const formatCount = (count, max = 100) => {
        return count >= max ? `${max}+` : count;
    };

    const shareText = buildShareText(shareTitle, shareCommunityName);

    const handleCopyLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopyStatus('success');
        } catch {
            // Fallback for browsers that don't support clipboard API
            try {
                const textArea = document.createElement('textarea');
                textArea.value = shareUrl;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const copied = document.execCommand('copy');
                document.body.removeChild(textArea);
                setCopyStatus(copied ? 'success' : 'error');
            } catch (fallbackErr) {
                console.error('Failed to copy link:', fallbackErr);
                setCopyStatus('error');
            }
        }
        // Reset status after a short delay so the user sees the feedback.
        // Clear any in-flight reset first so rapid re-copies don't leak timers.
        if (copyResetTimer.current) {
            clearTimeout(copyResetTimer.current);
        }
        copyResetTimer.current = setTimeout(() => setCopyStatus(null), 2000);
    };

    const sharePlatforms = [
        {
            name: 'Twitter / X',
            href: twitterShareUrl(shareUrl, shareText),
            icon: 'bi-twitter-x',
            label: 'Share on Twitter / X',
        },
        {
            name: 'Facebook',
            href: facebookShareUrl(shareUrl),
            icon: 'bi-facebook',
            label: 'Share on Facebook',
        },
        {
            name: 'LinkedIn',
            href: linkedInShareUrl(shareUrl),
            icon: 'bi-linkedin',
            label: 'Share on LinkedIn',
        },
    ];

    return (
        <div className="post-actions d-flex justify-content-between align-items-center py-2">
            <ButtonGroup className="post-action-buttons">
                {user && (
                    <>
                        <ControlLabelTooltip
                            label={
                                reaction === 'like'
                                    ? 'Unlike post'
                                    : 'Like post'
                            }
                        >
                            <Button
                                variant="link"
                                className="post-action-button text-decoration-none text-primary"
                                onClick={onLike}
                                aria-label={
                                    reaction === 'like'
                                        ? 'Unlike post'
                                        : 'Like post'
                                }
                            >
                                <i
                                    className={`bi ${reaction === 'like' ? 'bi-heart-fill' : 'bi-heart'}`}
                                />
                                <span className="ms-2">{likes}</span>
                            </Button>
                        </ControlLabelTooltip>

                        <ControlLabelTooltip
                            label={
                                reaction === 'dislike'
                                    ? 'Remove dislike'
                                    : 'Dislike post'
                            }
                        >
                            <Button
                                variant="link"
                                className="post-action-button text-decoration-none text-primary"
                                onClick={onDislike}
                                aria-label={
                                    reaction === 'dislike'
                                        ? 'Remove dislike'
                                        : 'Dislike post'
                                }
                            >
                                <i
                                    className={`bi ${reaction === 'dislike' ? 'bi-hand-thumbs-down-fill' : 'bi-hand-thumbs-down'}`}
                                />
                                <span className="ms-2">
                                    {formatCount(dislikes)}
                                </span>
                            </Button>
                        </ControlLabelTooltip>
                    </>
                )}

                <ControlLabelTooltip
                    label={showingReplies ? 'Hide replies' : 'View replies'}
                >
                    <Button
                        variant="link"
                        className={`post-action-button text-decoration-none ${
                            showingReplies
                                ? 'text-primary fw-semibold'
                                : 'text-primary'
                        }`}
                        onClick={onComment}
                        aria-label={
                            showingReplies ? 'Hide replies' : 'View replies'
                        }
                        aria-expanded={showingReplies}
                    >
                        <i
                            className={`bi ${showingReplies ? 'bi-chat-fill' : 'bi-chat'}`}
                        />
                        <span className="ms-2">{comments}</span>
                    </Button>
                </ControlLabelTooltip>

                {user && shareUrl ? (
                    <Dropdown as={ButtonGroup}>
                        <ControlLabelTooltip label="Share post">
                            <Dropdown.Toggle
                                variant="link"
                                className="post-action-button text-decoration-none text-primary share-dropdown-toggle"
                                aria-label="Share post"
                            >
                                <i className="bi bi-reply" />
                                <span className="ms-2">{shares}</span>
                            </Dropdown.Toggle>
                        </ControlLabelTooltip>
                        <Dropdown.Menu>
                            <Dropdown.Item
                                as="button"
                                type="button"
                                onClick={handleCopyLink}
                                aria-label="Copy link to post"
                                className="d-flex align-items-center"
                            >
                                <i
                                    className={`bi ${
                                        copyStatus === 'success'
                                            ? 'bi-check2'
                                            : 'bi-link-45deg'
                                    } me-2`}
                                    aria-hidden="true"
                                />
                                {copyStatus === 'success'
                                    ? 'Link copied!'
                                    : copyStatus === 'error'
                                      ? 'Copy failed'
                                      : 'Copy link'}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            {sharePlatforms.map(
                                ({ name, href, icon, label }) => (
                                    <Dropdown.Item
                                        key={name}
                                        as="a"
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={label}
                                        className="d-flex align-items-center"
                                    >
                                        <i
                                            className={`bi ${icon} me-2`}
                                            aria-hidden="true"
                                        />
                                        {name}
                                    </Dropdown.Item>
                                )
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                ) : null}
            </ButtonGroup>

            {user && (
                <ControlLabelTooltip
                    label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
                >
                    <Button
                        variant="link"
                        className="post-action-button text-decoration-none text-primary"
                        onClick={onBookmark}
                        aria-label={
                            isBookmarked ? 'Remove bookmark' : 'Add bookmark'
                        }
                    >
                        <i
                            className={`bi ${isBookmarked ? 'bi-bookmark-fill' : 'bi-bookmark'}`}
                        />
                    </Button>
                </ControlLabelTooltip>
            )}
        </div>
    );
};

PostActions.propTypes = {
    likes: PropTypes.number,
    dislikes: PropTypes.number,
    comments: PropTypes.number,
    shares: PropTypes.number,
    reaction: PropTypes.oneOf(['like', 'dislike', null]),
    isBookmarked: PropTypes.bool,
    showingReplies: PropTypes.bool,
    user: PropTypes.object,
    shareUrl: PropTypes.string,
    shareTitle: PropTypes.string,
    shareCommunityName: PropTypes.string,
    onLike: PropTypes.func.isRequired,
    onDislike: PropTypes.func.isRequired,
    onComment: PropTypes.func.isRequired,
    onBookmark: PropTypes.func.isRequired,
};

export default PostActions;
